import { useState } from 'react';
import { inviteAdmin } from '../../services/api';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Email, Person, PersonAdd, Phone } from '@mui/icons-material';

/* ── Design tokens ─────────────────────────────────────────────── */
const PRIMARY = '#6C98AD';
const PRIMARY_HOVER = '#5a8499';
const SECONDARY = '#24272a';
const CARD_SHADOW = '0 4px 20px rgba(108, 152, 173, 0.15)';
const FONT_HEADING = "'Avenir Next LT Pro', sans-serif";
const FONT_BODY = "'Avenir Next LT Pro', sans-serif";
const RADIUS = '1rem';
const TRANSITION = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

export default function InviteAdminPage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const result = await inviteAdmin(email, name, phone);
      setSuccess(result?.data?.message || 'Invitation sent successfully!');
      setEmail('');
      setName('');
      setPhone('');
    } catch (err) {
      setError(err.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <PersonAdd sx={{ fontSize: 32, color: PRIMARY }} />
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: SECONDARY, fontFamily: FONT_HEADING }}
        >
          Invite Admin
        </Typography>
      </Box>

      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={0}
        sx={{
          p: 4,
          borderRadius: RADIUS,
          boxShadow: CARD_SHADOW,
          border: '2px solid rgba(108, 152, 173, 0.15)',
        }}
      >
        <Typography variant="body1" sx={{ mb: 3, color: '#666', fontFamily: FONT_BODY }}>
          Send an invitation to a new administrator. They will receive an email with a link to complete their registration.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: RADIUS }}>
            {success}
          </Alert>
        )}

        <TextField
          label="Name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          sx={{
            mb: 2.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: RADIUS,
              fontFamily: FONT_BODY,
              '&.Mui-focused fieldset': { borderColor: PRIMARY },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person sx={{ color: PRIMARY }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Contact Number"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
          sx={{
            mb: 2.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: RADIUS,
              fontFamily: FONT_BODY,
              '&.Mui-focused fieldset': { borderColor: PRIMARY },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Phone sx={{ color: PRIMARY }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: RADIUS,
              fontFamily: FONT_BODY,
              '&.Mui-focused fieldset': { borderColor: PRIMARY },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email sx={{ color: PRIMARY }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading ? null : <PersonAdd />}
          sx={{
            py: 1.4,
            px: 4,
            borderRadius: RADIUS,
            fontWeight: 600,
            fontSize: '1rem',
            fontFamily: FONT_BODY,
            textTransform: 'none',
            background: `linear-gradient(135deg, ${PRIMARY} 0%, #8eb4c7 100%)`,
            boxShadow: '0 4px 12px rgba(108, 152, 173, 0.3)',
            transition: TRANSITION,
            '&:hover': {
              background: `linear-gradient(135deg, ${PRIMARY_HOVER} 0%, ${PRIMARY} 100%)`,
              boxShadow: '0 8px 20px rgba(108, 152, 173, 0.5)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Invitation'}
        </Button>
      </Paper>
    </Box>
  );
}
